import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import { campaignMedia } from '../data/mock';
import { Card, Label, Tag } from './primitives';

const src = (item, ext) => `/media/${item.file}.${ext}`;

/* Every card has the same anatomy so the grid reads as one set: a 4:5 media
   frame, then a body with a two-line title, the prize tag and the credit
   pinned to the bottom. Rows stretch, so all cards in a row match in height. */
const CardBody = ({ item, credit }) => (
  <div className="p-3 sm:p-4 flex-1 flex flex-col">
    <h4 className="text-[13px] sm:text-sm font-bold text-ink leading-snug line-clamp-2 min-h-[2.6em]">
      {item.label}
    </h4>
    <div className="mt-2">
      <Tag tone={item.tone}>{item.prize}</Tag>
    </div>
    <div className="mt-auto pt-2">{credit}</div>
  </div>
);

const creditClass =
  'inline-flex items-center gap-1 py-1 text-[11px] font-bold uppercase tracking-label text-subtle transition-colors';

/* The reel plays in place (never autoplays), so the card is not a link; the
   credit underneath links to the original post. */
const ReelCard = ({ item }) => (
  <Card className="p-0 overflow-hidden h-full flex flex-col">
    <div className="relative aspect-[4/5] bg-ink border-b-[1.5px] border-ink">
      <video
        src={src(item, 'mp4')}
        poster={src(item, 'jpg')}
        preload="none"
        controls
        playsInline
        aria-label={`${item.label} reel by ${item.credit}`}
        className="absolute inset-0 w-full h-full object-cover"
      />
      {item.plays && (
        <span className="absolute top-2 left-2 bg-marker text-ink text-[11px] font-bold px-2 py-0.5 rounded border border-ink pointer-events-none">
          {item.plays}
        </span>
      )}
    </div>
    <CardBody
      item={item}
      credit={
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`${creditClass} hover:text-flag`}
          aria-label={`View the original post by ${item.credit} on Instagram`}
        >
          {item.credit}
          <ArrowUpRight size={12} aria-hidden="true" />
        </a>
      }
    />
  </Card>
);

const PosterCard = ({ item }) => (
  <a
    href={item.url}
    target="_blank"
    rel="noopener noreferrer"
    className="group block h-full"
    aria-label={`${item.label}, ${item.prize}. View the original post by ${item.credit} on Instagram`}
  >
    <Card className="p-0 overflow-hidden h-full flex flex-col transition-all duration-200 group-hover:shadow-hard-lift group-hover:-translate-x-[2px] group-hover:-translate-y-[2px]">
      <img
        src={src(item, 'jpg')}
        loading="lazy"
        decoding="async"
        alt={`${item.label} launch post, ${item.prize} prize pool`}
        className="block w-full aspect-[4/5] object-cover border-b-[1.5px] border-ink"
      />
      <CardBody
        item={item}
        credit={
          <span className={`${creditClass} group-hover:text-flag`}>
            {item.credit}
            <ArrowUpRight size={12} aria-hidden="true" />
          </span>
        }
      />
    </Card>
  </a>
);

const GroupHeading = ({ title, count }) => (
  <div className="flex items-center gap-2.5 mb-4">
    <Label as="h3" className="text-ink">
      {title}
    </Label>
    <Tag tone="yellow">{count}</Tag>
  </div>
);

const Campaigns = () => {
  const builderfest = campaignMedia.filter((m) => m.group === 'builderfest');
  const global = campaignMedia.filter((m) => m.group === 'global');
  const reel = builderfest.find((m) => m.kind === 'reel');
  const posters = builderfest.filter((m) => m.kind === 'poster');

  return (
    <section
      id="campaigns"
      className="bg-paper border-y-[1.5px] border-ink py-16 sm:py-24 scroll-mt-20"
    >
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <Label className="mb-3">What I ran at Emergent</Label>
        <h2 className="text-2xl sm:text-4xl font-bold text-ink tracking-[-0.02em]">
          India to France. Kevin O'Leary to Fabrizio Romano.
        </h2>
        <p className="mt-3 text-sm sm:text-base text-ink-soft leading-relaxed max-w-2xl">
          Emergent turns a prompt into a deployed app. I helped take it to builders across India,
          Türkiye, Indonesia and France, ran the creator amplification behind two global
          campaigns, and managed the creator program across European and English-speaking
          markets.
        </p>
        <p className="mt-2 text-[13px] text-subtle leading-relaxed max-w-2xl">
          Launch posts from @emergentlabs and the creator reel from @rajshamani. I helped run these
          Builderfests and led the creator-marketing analytics behind them. Prize pools are as
          stated in each post.
        </p>

        {/* Builderfests */}
        <div className="mt-10">
          <GroupHeading title="Builderfests" count={builderfest.length} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-5 items-stretch">
            {reel && <ReelCard item={reel} />}
            {posters.map((item) => (
              <PosterCard key={item.file} item={item} />
            ))}
          </div>
        </div>

        {/* Global campaigns */}
        <div className="mt-12">
          <GroupHeading title="Global campaigns" count={global.length} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-5 items-stretch">
            {global.map((item) => (
              <PosterCard key={item.file} item={item} />
            ))}
            <Card tint="mint" className="col-span-2 p-5 sm:p-6 h-full flex flex-col justify-center">
              <Label className="mb-2.5 text-ink/60">Kevin O'Leary</Label>
              <p className="text-base sm:text-lg font-semibold text-ink leading-snug">
                Worked with Kevin O'Leary and built tools for him.
              </p>
              <p className="mt-3 text-sm text-ink-soft leading-relaxed">
                Builder Fest went global with Kevin O'Leary ($100K across 100 winners), alongside
                the Fabrizio Romano x Emergent Builder's Contest ($100K). I ran the creator
                amplification behind both.
              </p>
              <p className="mt-3 text-[13px] text-subtle leading-relaxed">
                Related side build: Mr. Wonderful's Deals, a source-verified dataset of his Shark
                Tank deals across 17 seasons (see Projects).
              </p>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Campaigns;
